type LightboxOpenedDetail = {
  content: HTMLDivElement | null;
  lightboxType: string | null;
  title: string;
};

type PlayerStateContext = {
  completed: boolean;
  milestones: Set<number>;
  modalTitle: string;
  player: YouTubePlayer;
  poller: number | null;
  started: boolean;
};

type YouTubePlayer = {
  getCurrentTime: () => number;
  getDuration: () => number;
  getIframe: () => HTMLIFrameElement;
  getVideoData: () => {
    title?: string;
  };
  getVideoUrl: () => string;
};

type YouTubeStateChangeEvent = {
  data: number;
};

type YouTubeNamespace = {
  Player: new (
    element: HTMLIFrameElement,
    options: {
      events: {
        onStateChange: (event: YouTubeStateChangeEvent) => void;
      };
    }
  ) => YouTubePlayer;
  PlayerState: {
    BUFFERING: number;
    ENDED: number;
    PAUSED: number;
    PLAYING: number;
  };
};

declare global {
  interface Window {
    YT?: YouTubeNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

const TRACKED_MILESTONES = [ 25, 50, 75 ];
const YOUTUBE_API_SRC = 'https://www.youtube.com/iframe_api';

export function isYouTubeEmbedUrl(url: string): boolean {
  return /youtube\.com\/embed|youtube-nocookie\.com\/embed/i.test(url);
}

export function normalizeYouTubeEmbedUrl(url: string): string {
  if (!url || !isYouTubeEmbedUrl(url)) {
    return url;
  }

  const parsedUrl = new URL(url, window.location.origin);

  parsedUrl.searchParams.set('enablejsapi', '1');
  parsedUrl.searchParams.set('origin', window.location.origin);

  return parsedUrl.toString();
}

export class VideoAnalytics {
  private static instance: VideoAnalytics | null = null;
  private apiReadyPromise: Promise<void> | null = null;
  private initialized = false;
  private players = new WeakMap<HTMLIFrameElement, PlayerStateContext>();

  private constructor() {}

  public static getInstance(): VideoAnalytics {
    if (!VideoAnalytics.instance) {
      VideoAnalytics.instance = new VideoAnalytics();
    }

    return VideoAnalytics.instance;
  }

  public init(): void {
    if (this.initialized) {
      return;
    }

    document.addEventListener(
      'startingsmarter:lightbox-opened',
      this.handleLightboxOpened as EventListener
    );

    this.initialized = true;
  }

  private handleLightboxOpened = (event: CustomEvent<LightboxOpenedDetail>): void => {
    const detail = event.detail;

    if (!detail?.content) {
      return;
    }

    const youtubeEmbeds = Array.from(detail.content.querySelectorAll('iframe')).filter((iframe) =>
      isYouTubeEmbedUrl(iframe.src)
    );

    if (!youtubeEmbeds.length) {
      return;
    }

    youtubeEmbeds.forEach((iframe) => {
      const normalizedUrl = normalizeYouTubeEmbedUrl(iframe.src);

      if (iframe.src !== normalizedUrl) {
        iframe.src = normalizedUrl;
      }

      this.ensureYouTubeApiReady().then(() => {
        this.initializePlayer(iframe, detail.title);
      });
    });
  };

  private ensureYouTubeApiReady(): Promise<void> {
    if (window.YT?.Player) {
      return Promise.resolve();
    }

    if (this.apiReadyPromise) {
      return this.apiReadyPromise;
    }

    this.apiReadyPromise = new Promise((resolve) => {
      const previousCallback = window.onYouTubeIframeAPIReady;

      window.onYouTubeIframeAPIReady = () => {
        previousCallback?.();
        resolve();
      };

      if (!document.querySelector(`script[src="${YOUTUBE_API_SRC}"]`)) {
        const script = document.createElement('script');
        script.src = YOUTUBE_API_SRC;
        script.async = true;
        document.head.appendChild(script);
      }
    });

    return this.apiReadyPromise;
  }

  private initializePlayer(iframe: HTMLIFrameElement, modalTitle: string): void {
    if (!window.YT?.Player || this.players.has(iframe)) {
      return;
    }

    const player = new window.YT.Player(iframe, {
      events: {
        onStateChange: (event) => this.handlePlayerStateChange(event, iframe),
      },
    });

    this.players.set(iframe, {
      completed: false,
      milestones: new Set<number>(),
      modalTitle,
      player,
      poller: null,
      started: false,
    });
  }

  private handlePlayerStateChange(event: YouTubeStateChangeEvent, iframe: HTMLIFrameElement): void {
    const context = this.players.get(iframe);

    if (!context || !window.YT?.PlayerState) {
      return;
    }

    if (event.data === window.YT.PlayerState.PLAYING) {
      if (!context.started) {
        context.started = true;
        this.trackPlayerEvent('video_start', context, { video_percent: 0 });
      }

      this.startProgressPolling(context);
      return;
    }

    if (event.data === window.YT.PlayerState.ENDED) {
      this.trackProgressMilestones(context, 100);

      if (!context.completed) {
        context.completed = true;
        this.trackPlayerEvent('video_complete', context, { video_percent: 100 });
      }

      this.stopProgressPolling(context);
      return;
    }

    if (
      event.data === window.YT.PlayerState.PAUSED ||
      event.data === window.YT.PlayerState.BUFFERING
    ) {
      this.stopProgressPolling(context);
    }
  }

  private startProgressPolling(context: PlayerStateContext): void {
    if (context.poller) {
      return;
    }

    context.poller = window.setInterval(() => {
      if (!document.contains(context.player.getIframe())) {
        this.stopProgressPolling(context);
        return;
      }

      const duration = context.player.getDuration();

      if (!duration) {
        return;
      }

      const progressPercent = Math.floor((context.player.getCurrentTime() / duration) * 100);
      this.trackProgressMilestones(context, progressPercent);
    }, 1000);
  }

  private stopProgressPolling(context: PlayerStateContext): void {
    if (!context.poller) {
      return;
    }

    window.clearInterval(context.poller);
    context.poller = null;
  }

  private trackProgressMilestones(context: PlayerStateContext, progressPercent: number): void {
    TRACKED_MILESTONES.forEach((milestone) => {
      if (progressPercent < milestone || context.milestones.has(milestone)) {
        return;
      }

      context.milestones.add(milestone);

      this.trackPlayerEvent('video_progress', context, {
        video_percent: milestone,
      });
    });
  }

  private trackPlayerEvent(
    eventName: 'video_start' | 'video_progress' | 'video_complete',
    context: PlayerStateContext,
    extraParams: Record<string, number>
  ): void {
    if (typeof window.gtag !== 'function') {
      return;
    }

    const videoTitle = context.modalTitle || context.player.getVideoData().title || 'Video Modal';

    window.gtag('event', eventName, {
      event_category: 'Video Modal',
      event_label: videoTitle,
      video_provider: 'youtube',
      video_title: videoTitle,
      video_url: context.player.getVideoUrl(),
      ...extraParams,
    });
  }
}

export const videoAnalytics = VideoAnalytics.getInstance();

export default VideoAnalytics.getInstance();
